import { useEffect, useRef, useState } from 'react'
import { Drawer } from './drawer'
import { useProgramInfo } from '../hooks/useProgramInfo'
import { Channel } from '../types'
import { useUserSelfcare } from '../hooks/useUserSelfcare'
import {
  BoxRecordingMessage,
  LoginMessage,
  ProgramHeader,
  ProgramImage,
  RecordButtons,
} from './program-drawer-components'
import { useIsProgramRecording } from '../hooks/useIsProgramRecording'
import { ChannelEpisodesTabs } from './program-drawer-components/channel-episode-tabs'
import { FormMessages } from '@ddg-frontend/inputs'
import { useProgramOtherEpisodes, useProgramOtherSchedules } from '../hooks'
import { useApp } from './app-context'
import { getResource } from '../utils'
import { useChannelInfo } from '../hooks/useChannelInfo'
import { decodeUserSelfcare } from '../utils/decode-user-selfcare-info'

interface ProgramDrawerProps {
  programId: number | undefined
  channel?: Channel
  onProgramClick: (id: number | undefined, channel: Channel | undefined) => void
  date?: string
}
export const ProgramDrawer = ({
  programId,
  channel,
  onProgramClick,
  date,
}: ProgramDrawerProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const urlParams = new URLSearchParams(window.location.search)
  const programIdParam = urlParams.get('programId')
  const callLetterParam = urlParams.get('channel')
  const isSapo = !!import.meta.env.VITE_IS_SAPO
  const dateParam = urlParams.get('date')
  const { channelData } = useApp()
  const channelFromParams = channelData?.find(
    (c) => c.callLetter === callLetterParam
  )
  const { data: channelInfo } = useChannelInfo(
    callLetterParam || channel?.callLetter || ''
  )
  const { data, isLoading, error } = useProgramInfo(
    programId || Number(programIdParam),
    date || dateParam || undefined
  )
  const { data: userSelfcare } = useUserSelfcare()
  const { data: userRecording, isLoading: isLoadingRecording } =
    useIsProgramRecording(data?.programId || 0)
  const { data: episodesData, isLoading: isEpisodesDataLoading } =
    useProgramOtherEpisodes(
      channel?.callLetter || callLetterParam || '',
      data?.titleId || ''
    )
  const { data: schedulesData, isLoading: isSchedulesDataLoading } =
    useProgramOtherSchedules(
      programIdParam ? data?.programId.toString() || undefined : undefined
    )

  useEffect(() => {
    if (programId || programIdParam) {
      setIsOpen(true)
    } else {
      setIsOpen(false)
      //removel all params from url
    }
  }, [programId, programIdParam])

  useEffect(() => {
    if (programId && drawerContentRef.current) {
      drawerContentRef.current.scrollTop = 0
    }
  }, [programId])

  const isProgramInThePast = data?.end.date && data?.end.date < new Date()
  const { alias, commercialOffer, contact } = decodeUserSelfcare(userSelfcare)
  const selectedBox = `${commercialOffer}  ${alias ? `- ${alias}` : ''}  ${
    contact ? `(${contact})` : ''
  }`

  const isUserAuthenticated =
    window.ISites.MEOGo.Authentication.isOauthAuthenticated()
  const drawerContentRef = useRef<HTMLDivElement>(null)

  return (
    <Drawer
      drawerClassName="program-drawer"
      isOpen={isOpen}
      onClose={() => onProgramClick(undefined, undefined)}
    >
      {error && (
        <FormMessages
          type={'error'}
          title="Ocorreu um erro a carregar o programa"
          className="program-drawer-error"
        />
      )}
      {isLoading && <div className="block-loading" />}
      <button
        type="button"
        className="close-button close-overlay"
        onClick={() => onProgramClick(undefined, undefined)}
      >
        <span className="screen-readers-only">Fechar detalhe de programa</span>
      </button>
      {data && !isLoading && (programId || !!programIdParam) && (
        <div ref={drawerContentRef}>
          {data && (channel || channelFromParams) && (
            <ProgramImage
              title={data?.title}
              titleId={data?.titleId}
              callLetter={data?.callLetter}
              friendlyUrlName={data?.friendlyUrlName}
              startDate={data?.start.date}
              endDate={data?.end.date}
              programId={String(data?.programId)}
              availableOnMeoGo={channel?.MEOGo.isAvailable || false}
            />
          )}

          <div className="program-drawer-content">
            {data && (channel || channelFromParams) && (
              <ProgramHeader
                channelTitle={channel?.title || channelFromParams?.title || ''}
                programTitle={data?.title}
                start={data?.start}
                end={data?.end}
                userRecording={userRecording}
                nextProgram={episodesData?.find(
                  (episode) =>
                    episode.seriesEpisodeNumber ===
                    data!.seriesEpisodeNumber + 1
                )}
                previousProgram={episodesData?.find(
                  (episode) =>
                    episode.seriesEpisodeNumber ===
                    data!.seriesEpisodeNumber - 1
                )}
                onProgramClick={(programId) =>
                  onProgramClick(programId, channel || channelFromParams)
                }
              />
            )}
            {!isUserAuthenticated && !isSapo && <LoginMessage />}
            {selectedBox &&
              commercialOffer &&
              !isProgramInThePast &&
              !isSapo && <BoxRecordingMessage selectedBox={selectedBox} />}
            {isUserAuthenticated && data && !isLoadingRecording && !isSapo && (
              <RecordButtons
                program={data}
                userRecording={userRecording}
                drawerContentRef={drawerContentRef}
                schedules={schedulesData}
              />
            )}

            <div className="program-description">{data?.synopsis}</div>
            {channelInfo?.moreInfoUrl && (
              <a
                href={channelInfo.moreInfoUrl}
                target="_blank"
                className="btn-text"
              >
                {getResource('ProgramDetail.KnowChannel')}
              </a>
            )}
            {data && (
              <ChannelEpisodesTabs
                programTitle={data?.title}
                id={data?.id}
                seriesEpisodeNumber={data?.seriesEpisodeNumber}
                onProgramClick={(programId) =>
                  onProgramClick(programId, channel || channelFromParams)
                }
                episodesData={episodesData}
                isEpisodesDataLoading={isEpisodesDataLoading}
                schedulesData={schedulesData}
                isSchedulesDataLoading={isSchedulesDataLoading}
                utcStartDay={data?.start.utc}
                availableOnChannel={
                  channelFromParams?.MEOGo.isAvailable || false
                }
              />
            )}
          </div>
        </div>
      )}
    </Drawer>
  )
}
